import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLookupsStore } from '../../stores/lookupsStore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ entry, hasIsActive, hasAccessLevel, onEdit, onToggleActive, onDelete, isEditing, editValue, editAccessLevel, onEditLabel, onEditAccessLevel, onSaveEdit, onCancelEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="surface border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3"
    >
      <div className="flex items-center justify-between">
        {/* Drag Handle + Content */}
        <div className="flex items-center flex-1">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-move mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Drag to reorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>

          {/* Label (editable) */}
          <div className="flex-1">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => onEditLabel(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                    maxLength={64}
                    autoFocus
                    placeholder="Label"
                  />
                  {hasAccessLevel && (
                    <input
                      type="number"
                      value={editAccessLevel}
                      onChange={(e) => onEditAccessLevel(parseInt(e.target.value) || 0)}
                      className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                      min="0"
                      max="255"
                      placeholder="Access Level"
                    />
                  )}
                </div>
                <button
                  onClick={onSaveEdit}
                  className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  title="Save"
                >
                  ✓
                </button>
                <button
                  onClick={onCancelEdit}
                  className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div>
                <p
                  className="text-primary font-medium cursor-pointer hover:text-primary-600 dark:hover:text-primary-400"
                  onClick={() => onEdit(entry.label, entry.access_level)}
                >
                  {entry.label}
                  {hasAccessLevel && (
                    <span className="ml-2 text-xs text-secondary">
                      (Access: {entry.access_level})
                    </span>
                  )}
                </p>
                <p className="text-xs text-secondary">{entry.code}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          {/* Active Toggle */}
          {hasIsActive && !isEditing && (
            <button
              onClick={() => onToggleActive(entry)}
              className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                entry.is_active
                  ? 'bg-primary-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
              title={entry.is_active ? 'Active' : 'Inactive'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  entry.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          )}

          {/* Delete Button */}
          {!isEditing && (
            <button
              onClick={() => onDelete(entry)}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              title="Delete"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const LookupsWidget = ({ tableName }) => {
  const { accessToken, user } = useAuthStore();
  const { entries, hasIsActive, fetchEntries, createEntry, updateEntry, deleteEntry, reorderEntries, isLoading, error } = useLookupsStore();

  const [localEntries, setLocalEntries] = useState([]);
  const [newEntryLabel, setNewEntryLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editAccessLevel, setEditAccessLevel] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isAdmin = user?.is_admin === 1;
  const hasAccessLevel = tableName === 'lk_role';

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (tableName && accessToken) {
      fetchEntries(tableName, accessToken);
    }
  }, [tableName, accessToken, fetchEntries]);

  useEffect(() => {
    setLocalEntries(entries);
  }, [entries]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localEntries.findIndex((item) => item.id === active.id);
      const newIndex = localEntries.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(localEntries, oldIndex, newIndex);
      setLocalEntries(newOrder);

      // Send new order to backend
      const order = newOrder.map((item) => item.id);
      await reorderEntries(tableName, order, accessToken);

      // Refresh to get updated sort_order values
      await fetchEntries(tableName, accessToken);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntryLabel.trim()) return;

    const result = await createEntry(tableName, newEntryLabel, accessToken);
    if (result.success) {
      setNewEntryLabel('');
    }
  };

  const handleStartEdit = (entryId, label, accessLevel = 0) => {
    setEditingId(entryId);
    setEditValue(label);
    setEditAccessLevel(accessLevel || 0);
  };

  const handleSaveEdit = async (entryId) => {
    if (!editValue.trim()) return;

    const updates = { label: editValue };
    if (hasAccessLevel) {
      updates.access_level = editAccessLevel;
    }

    const result = await updateEntry(tableName, entryId, updates, accessToken);
    if (result.success) {
      setEditingId(null);
      setEditValue('');
      setEditAccessLevel(0);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditAccessLevel(0);
  };

  const handleToggleActive = async (entry) => {
    const result = await updateEntry(tableName, entry.id, { is_active: !entry.is_active }, accessToken);
    if (result.success) {
      // Refresh data to show updated state
      await fetchEntries(tableName, accessToken);
    }
  };

  const handleDeleteClick = (entry) => {
    setDeleteConfirm(entry);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    const result = await deleteEntry(tableName, deleteConfirm.id, accessToken);
    if (result.success) {
      setDeleteConfirm(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  if (isLoading && localEntries.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && localEntries.length === 0) {
    return (
      <div className="surface rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-semibold text-primary mb-4">
        {tableName ? tableName.replace('lk_', '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Lookup Entries'}
      </h3>

      {/* Add New Entry */}
      <div className="mb-6 flex gap-2">
        <input
          type="text"
          value={newEntryLabel}
          onChange={(e) => setNewEntryLabel(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddEntry()}
          placeholder="Enter new entry label..."
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
          maxLength={64}
        />
        <button
          onClick={handleAddEntry}
          disabled={!newEntryLabel.trim()}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>

      {/* Entries List with Drag and Drop */}
      {localEntries.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localEntries.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            {localEntries.map((entry) => (
              <SortableItem
                key={entry.id}
                entry={entry}
                hasIsActive={hasIsActive}
                hasAccessLevel={hasAccessLevel}
                onEdit={() => handleStartEdit(entry.id, entry.label, entry.access_level)}
                onToggleActive={handleToggleActive}
                onDelete={handleDeleteClick}
                isEditing={editingId === entry.id}
                editValue={editValue}
                editAccessLevel={editAccessLevel}
                onEditLabel={setEditValue}
                onEditAccessLevel={setEditAccessLevel}
                onSaveEdit={() => handleSaveEdit(entry.id)}
                onCancelEdit={handleCancelEdit}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <p className="text-center text-secondary py-8">No entries found</p>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-primary mb-4">Confirm Delete</h3>
            <p className="text-secondary mb-6">
              Are you sure you want to delete "{deleteConfirm.label}"?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupsWidget;
